import tokens from '@kluein/design-tokens';
import { Box, Heading, Main } from '@kluein/klue-ui';
import { Cards, Star } from '@phosphor-icons/react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';

import { KlueSpinner } from 'components/common/klue-spinner';
import { useAuth } from 'contexts/auth';
import { useDashboardFilter } from 'hooks/dashboard';
import { useAlertDashboard } from 'hooks/dashboard/use-alert-dashboard';
import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';
import { useDocumentTitle } from 'hooks/use-document-title';
import { useCompanyFavorite } from 'pages/company/hooks';
import { KnowledgeHubWidget } from 'pages/knowledge-hub/partials/knowledge-hub-widget/KnowledgeHubWidget';
import store from 'store';
import TEST_IDS from 'test-ids';

import { DASHBOARD_DESKTOP_MAX_WIDTH } from './dashboard.constants';
import { AskKlue } from './partials/ask-klue';
import { CuratorControls } from './partials/curator-controls';
import { DashboardFavorites } from './partials/dashboard-favorites';
import { DashboardRivals } from './partials/dashboard-rivals';
import { DashboardSectionHeader as Header } from './partials/dashboard-with-news/DashboardWithNews.styles';
import { FilterDrops } from './partials/filter-drops';

import type { RivalGroupType } from 'api/api.types';
import type { Dispatch, RootState } from 'store/store.types';

const DashboardWithNews = lazy(
  () => import('./partials/dashboard-with-news/DashboardWithNews'),
);

// Lazy — the modal pulls papaparse + @dnd-kit + Mantine internals; only
// curators without the `disableNewCreateBoard` FF ever open it, so keep
// the chunk out of the dashboard bundle.
const NewBoardModal = lazy(() =>
  import('./partials/new-board-modal').then((m) => ({
    default: m.NewBoardModal,
  })),
);

export default function DashboardView() {
  const { t } = useTranslation(['Common', 'Dashboard', 'News']);

  useDocumentTitle(t('Common:page.titles.default'));
  const {
    rivalGroups: { fetchRivalGroupsFromApi },
  } = useDispatch<Dispatch>();
  const {
    isCompanyFavoritesEnabled,
    isCurator,
    isDashboardNewsEnabled,
    isAskKlueEnabled,
    isKnowledgeHubEnabled,
    isUnifiedSearchEnabled,
  } = useAuth();
  const { sort, setSort, group, setGroup, rivalGroup } = useDashboardFilter();
  const { disableNewCreateBoard } = useCompanyFeatureFlags(
    'disableNewCreateBoard',
  );
  const [{ create }, setQuery] = useQueryParams({ create: StringParam });
  const isNewBoardModalOpen =
    !disableNewCreateBoard && isCurator && create === 'board';
  const { userCompanyFavorites } = useCompanyFavorite();
  const { isLoadingAlertsCount } = useAlertDashboard();
  // Check for transition immediately during render to prevent flash
  const location = useLocation();
  const navigate = useNavigate();

  // Track if we need to trigger animation - preserve this even after router state is cleared
  const [shouldAnimate] = useState(
    () => location.state?.transition === 'askklue-to-dashboard',
  );

  // Clear router state after initial render to prevent repeated animations
  useEffect(() => {
    if (location.state?.transition === 'askklue-to-dashboard') {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null },
      );
    }
  }, [location.pathname, location.search, location.state, navigate]);
  useEffect(() => {
    fetchRivalGroupsFromApi(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRivalGroupsFromApi]);

  const allRivalGroups = useSelector<RootState, RivalGroupType[]>(
    store.select.rivalGroups.getSortedRivalGroups,
  );

  // Trigger animation when dashboard finishes loading
  const isLoading = useSelector<RootState, boolean>(
    store.select.dashboard.isLoading,
  );

  // Create ref for animation target
  const containerRef = useRef<HTMLDivElement>(null);

  if (!shouldAnimate && (isLoading || isLoadingAlertsCount)) {
    return (
      <>
        <Box
          fill
          align="center"
          justify="center"
          data-test-id={TEST_IDS.cardViewer.loading}
        >
          <KlueSpinner />
        </Box>
      </>
    );
  }

  return (
    <Box fill flex="grow" id="dashboard-view-container" ref={containerRef}>
      {isNewBoardModalOpen && (
        <Suspense fallback={null}>
          <NewBoardModal
            onClose={() => setQuery({ create: undefined }, 'replaceIn')}
          />
        </Suspense>
      )}
      {isCurator && <CuratorControls sort={sort} group={group} />}

      {isKnowledgeHubEnabled && <KnowledgeHubWidget />}

      <Box>
        {(() => {
          if (isDashboardNewsEnabled) {
            return (
              <DashboardWithNews
                companyFavorites={userCompanyFavorites}
                rivalGroups={allRivalGroups}
                rivalGroup={rivalGroup}
                group={group}
                setGroup={setGroup}
                sort={sort}
                setSort={setSort}
              />
            );
          }

          return (
            <Main
              data-test-id={TEST_IDS.dashboard.container}
              margin="0 auto"
              width={DASHBOARD_DESKTOP_MAX_WIDTH}
              pad={{
                horizontal: 'xxlarge',
                top:
                  isAskKlueEnabled && !isUnifiedSearchEnabled
                    ? 'large'
                    : 'xxlarge',
                bottom: '0',
              }}
            >
              {isAskKlueEnabled && (
                <Box
                  margin={{ bottom: 'xlarge' }}
                  height={{ min: '44px' }}
                  flex="grow"
                >
                  <AskKlue />
                </Box>
              )}
              {isCompanyFavoritesEnabled && userCompanyFavorites.length ? (
                <>
                  <Header pad="medium">
                    <Box direction="row" align="center">
                      <Star color={tokens.color.neutral[1000]} size="24px" />
                      <Heading
                        level="2"
                        size="large"
                        lineHeight="dense"
                        margin={{ left: 'xsmall', vertical: '0' }}
                      >
                        {t('Common:favorites')}
                      </Heading>
                    </Box>
                  </Header>
                  <DashboardFavorites favorites={userCompanyFavorites} />
                </>
              ) : null}
              <Header pad="medium">
                <Box direction="row" align="center">
                  <Cards color={tokens.color.neutral[1000]} size="24px" />
                  <Heading
                    level="2"
                    size="large"
                    lineHeight="dense"
                    margin={{ left: 'xsmall', vertical: '0' }}
                  >
                    {t('Common:boards')}
                  </Heading>
                </Box>
                <Box direction="row" gap="xsmall">
                  <FilterDrops
                    rivalGroups={allRivalGroups}
                    onSelectSort={setSort}
                    sort={sort}
                    onSelectGroup={setGroup}
                    group={group}
                  />
                </Box>
              </Header>
              <DashboardRivals
                rivalGroups={allRivalGroups}
                group={group}
                sort={sort}
              />
            </Main>
          );
        })()}
      </Box>
    </Box>
  );
}
